import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { ReplaySubject, Subscription } from 'rxjs';
import { ValidationErrorService } from 'src/app/core/services/error/validation-error/validation-error.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Input atom
 */
export class InputComponent implements OnInit, OnDestroy {
  @Input() hint: string = '';
  @Input() label!: string;
  @Input() fieldName: string = '';
  @Input() type: string = '';
  @Input() control!: FormControl;
  @Input() datepicker?: any;
  @Input() textarea?: boolean;
  @Input() fontSize?: string;
  @Input() prefix?: string;
  @Input() suffix?: string;
  @Input() prefixIcon?: string;
  @Input() separator?: string;
  @Input() mask?: string;
  @Input() minDate!: Date;
  @Input() maxDate!: Date;
  @Input() placeholder?: string;
  @Input() formGroup?: FormGroup;
  @Input() textareaMaxLength?: number;
  @Input() customErrorMessage: string | null = null;
  @Output() searchClick = new EventEmitter<any>();
  errorMessage = '';
  translatedFieldName = '';
  searchDisabled?: boolean;
  floatLabel: 'auto' | 'always' | 'never' = 'auto';
  private subsink = new Subscription();

  private _required: boolean = false;
  isPasswordVisible: boolean = false;
  private _readonly: boolean = false;
  maxLength!: number;
  private _disabled: boolean = false;
  private _hasErrorHint: boolean = true;
  private disabled$: ReplaySubject<boolean> = new ReplaySubject();

  /**
   * @constructor
   */
  constructor(
    private validationErrorService: ValidationErrorService,
    private translationService: TranslationService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    if (this.fieldName) {
      this.translationService
        .translateString(this.fieldName)
        .then((translated) => {
          this.translatedFieldName = translated;
        });
    }

    this.setErrorMessage(this.control);
    const subscription = this.control.statusChanges.subscribe(() => {
      this.setErrorMessage(this.control);
    });
    this.subsink.add(subscription);
    this.maxLength = this.validationErrorService.getMaximumLength(this.control);
    this.subsink.add(
      this.disabled$.subscribe((disabled) => {
        if (disabled) {
          setTimeout(() => this.control.disable());
        } else {
          setTimeout(() => this.control.enable());
        }
      })
    );
  }

  /**
   * clean up subscriptions
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Whether this input is required or not
   */
  @Input()
  get required() {
    return this._required;
  }

  /**
   * Sets this input as required
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set required(value: any) {
    this._required = coerceBooleanProperty(value);
  }

  /**
   * Whether this input is read-only or not
   */
  @Input()
  get readonly() {
    return this._readonly;
  }

  /**
   * Sets this input as read-only
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set readonly(value: any) {
    this._readonly = coerceBooleanProperty(value);
  }

  /**
   * Whether this input is read-only or not
   */
  @Input()
  get errorHint() {
    return this._hasErrorHint;
  }

  /**
   * Sets this input as read-only
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set errorHint(value: any) {
    this._hasErrorHint = coerceBooleanProperty(value);
  }

  /**
   * Whether this input has a hint attached
   * @return {boolean}
   */
  hasHint(): boolean {
    return this.hint?.length > 0;
  }

  /**
   * Whether this input is a password or not
   * @return {boolean}
   */
  isPassword(): boolean {
    return this.type === 'password';
  }

  /**
   * Whether this input is a search or not
   * @return {boolean}
   */
  isSearch(): boolean {
    return this.type === 'search';
  }

  /**
   * Emits search click
   */
  searchClickAction(): void {
    this.searchClick.emit();
  }

  /**
   * Which password icon to render
   * @return {string}
   */
  getPasswordIcon(): string {
    return 'visibility' + (this.isPasswordVisible ? '_off' : '');
  }

  /**
   * Toggles visiblity factor
   */
  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  /**
   * Decides input type, which depends on visibility factor
   * @return {string}
   */
  get calculatedType(): string {
    if (this.type === 'password' && this.isPasswordVisible) {
      return 'text';
    }
    return this.type;
  }

  /**
   * Whether this input is a password or not
   * @return {boolean}
   */
  isLoading(): boolean {
    return this.type === 'loading';
  }

  /**
   * Sets error message of this input
   * @param {AbstractControl} abstractControl
   */
  setErrorMessage(abstractControl: AbstractControl) {
    this.validationErrorService
      .translateError(abstractControl, '', this.translatedFieldName)
      .then((errorMessage) => {
        this.errorMessage = errorMessage;
      });
  }

  /**
   * Whether this form field is disabled or not
   */
  @Input()
  get disabled() {
    return this._disabled;
  }

  /**
   * Sets this form field as disabled
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set disabled(value: any) {
    this._disabled = coerceBooleanProperty(value);
    this.disabled$.next(this._disabled);
  }

  /**
   * Verifies if an icon is being used in the prefix.
   * If it is, blocks some css that messes up position of the icon relative to the input.
   */
  get hasPrefixIcon(): boolean {
    return !!this.prefixIcon;
  }

  /**
   * Blocks characters that are invalid in integer number inputs (e, E, +, ,).
   * @param {KeyboardEvent} event
   */
  onNumberKeydown(event: KeyboardEvent): void {
    if (this.type === 'number' && ['e', 'E', '+', ','].includes(event.key)) {
      event.preventDefault();
    }
  }
}
